import { zodResolver } from "@hookform/resolvers/zod";
import { roleSchema } from "@orsane/schema/users";
import { UserPlus } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useInviteUser } from "@/mutations/user-mutations";
import EnterButton from "../buttons/EnterButton";
import { Field, FieldError, FieldLabel } from "../ui/field";

const inviteSchema = z.object({
    email: z.string().email("Please enter a valid email address"),
    role: roleSchema.default("member"),
});

type InviteFormData = z.infer<typeof inviteSchema>;

interface InviteTeamMemberDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function InviteTeamMemberDialog({ open, onOpenChange }: InviteTeamMemberDialogProps) {
    const inviteUser = useInviteUser();

    const form = useForm({
        resolver: zodResolver(inviteSchema),
        defaultValues: {
            email: "",
            role: "member",
        },
    });

    const onSubmit = async (data: InviteFormData) => {
        try {
            await inviteUser.mutateAsync(data);
            toast.success("Invitation sent", {
                description: `An invitation has been sent to ${data.email}`,
            });
            form.reset();
            onOpenChange(false);
        } catch (error) {
            toast.error("Failed to send invitation", {
                description: error instanceof Error ? error.message : "An unexpected error occurred",
            });
        }
    };

    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen) {
            form.reset();
        }
        onOpenChange(newOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[480px] gap-6 rounded-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg font-medium">
                        <UserPlus className="size-4" />
                        Invite Team Member
                    </DialogTitle>
                    <DialogDescription>They will receive an email with instructions to join your team.</DialogDescription>
                </DialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <Controller
                        control={form.control}
                        name="email"
                        render={({ field, fieldState }) => (
                            <Field>
                                <FieldLabel>Email</FieldLabel>
                                <Input type="email" placeholder="Enter email address" {...field} />
                                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                            </Field>
                        )}
                    />

                    <Controller
                        control={form.control}
                        name="role"
                        render={({ field, fieldState }) => (
                            <Field className="gap-1">
                                <FieldLabel>Role</FieldLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <SelectTrigger className="min-w-32">
                                        <SelectValue>{(v: string) => (v === "admin" ? "Admin" : "Member")}</SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            <SelectItem value="member">User</SelectItem>
                                            <SelectItem value="admin">Admin</SelectItem>
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                            </Field>
                        )}
                    />

                    <DialogFooter>
                        <Button type="button" size="sm" variant="outline" onClick={() => handleOpenChange(false)}>
                            Cancel
                        </Button>
                        <EnterButton type="submit" disabled={inviteUser.isPending} isSubmitting={inviteUser.isPending}>
                            {inviteUser.isPending ? "Sending..." : "Send Invitation"}
                        </EnterButton>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
